/**
 * MedlibQrBanner — bandeau d'annonce accueil (lien vers MedLib).
 *
 * Affichage : UNIQUEMENT sur la Home, position fixed au-dessus de la Navbar.
 * Dismiss persisté dans localStorage ("om_medlib_qr_dismissed"=1).
 * Clic sur le bandeau → ouvre https://medlib.app dans un nouvel onglet.
 */
import { useEffect, useState } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { useT } from '@/lib/i18n';

const DISMISS_KEY = 'om_medlib_qr_dismissed';
const MEDLIB_URL = 'https://medlib.app';
const HEALTH_ACCENT = '#10B981';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 640px)');
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener?.('change', sync);
    return () => mq.removeEventListener?.('change', sync);
  }, []);
  return isMobile;
}

export function MedlibQrBanner() {
  const t = useT();
  const [visible, setVisible] = useState(false);
  const isMobile = useIsMobile();

  // Lecture du flag dismiss au mount uniquement (évite CLS).
  useEffect(() => {
    try {
      const dismissed = window.localStorage.getItem(DISMISS_KEY) === '1';
      if (!dismissed) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  // Synchronise `--om-banner-h` (lu par Navbar.jsx) pour décaler la nav fixed.
  useEffect(() => {
    const root = document.documentElement;
    const bannerHeight = visible ? (isMobile ? '52px' : '44px') : '0px';
    root.style.setProperty('--om-banner-h', bannerHeight);
    return () => {
      root.style.setProperty('--om-banner-h', '0px');
    };
  }, [visible, isMobile]);

  const dismiss = (ev) => {
    ev?.stopPropagation?.();
    ev?.preventDefault?.();
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore quota */
    }
    setVisible(false);
  };

  return (
    <div
      aria-hidden={!visible}
      className={`fixed inset-x-0 top-0 z-[60] ${visible ? '' : 'pointer-events-none'}`}
      data-testid="medlib-qr-banner-wrapper"
    >
      {visible && (
        <a
          href={MEDLIB_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="medlib-qr-banner"
          className="group relative flex min-h-[44px] items-center justify-center gap-2.5 px-10 py-2 text-sm text-[var(--om-ink)] no-underline transition-colors"
          style={{
            background:
              'linear-gradient(90deg, rgba(16,185,129,0.14) 0%, rgba(16,185,129,0.22) 50%, rgba(16,185,129,0.14) 100%)',
            borderBottom: '1px solid rgba(16,185,129,0.35)',
            backdropFilter: 'saturate(130%) blur(6px)',
          }}
        >
          <img
            src="/apps/medlib-mark.svg"
            alt=""
            aria-hidden
            width="24"
            height="24"
            className="shrink-0 rounded-md shadow-[0_2px_6px_-2px_rgba(13,71,161,0.6)]"
          />
          <span className="min-w-0 flex-shrink text-center text-[13px] font-semibold leading-tight sm:text-sm">
            <span style={{ color: HEALTH_ACCENT, fontWeight: 800 }}>MedLib</span>
            <span className="mx-1.5 opacity-60">:</span>
            <span>{t('medlib_qr.banner_text')}</span>
          </span>
          <ExternalLink
            size={13}
            strokeWidth={2.6}
            className="shrink-0 opacity-60 transition-transform group-hover:translate-x-0.5 group-hover:opacity-100"
            aria-hidden
          />

          <button
            type="button"
            onClick={dismiss}
            aria-label={t('medlib_qr.dismiss_aria')}
            className="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full text-[var(--om-muted)] transition-colors hover:bg-black/5 hover:text-[var(--om-ink)] dark:hover:bg-white/10"
            data-testid="medlib-qr-dismiss"
          >
            <X size={13} strokeWidth={2.6} />
          </button>
        </a>
      )}
    </div>
  );
}
